/**
 * C-GTCC-VIEW-DIAG-AIA1 — GTCC view "AI & Analytics", 1:1 after
 * `GTCC_REFERENCE_2026-09-01.html`, `div[data-v="aia"]` (lines 958-990).
 *
 * Layout of the reference, element for element:
 *   - head "AI & Analytics" with the freshness badge and the `.sub` line of
 *     line 959 (its trailing "Beispieldaten." is dropped — this view shows
 *     measurements),
 *   - `.grid-main`, leading column — "Provider-Workbench (Staging)" and
 *     "Question Analytics",
 *   - `.grid-main`, trailing column — "GI Coverage Gaps", "AI-Nutzung je Tenant".
 *
 * P19 folds the old views `aiwb`, `gicov` and `aihealth` into this one. All
 * four panels sit on endpoints that already exist; nothing here is a new
 * route and nothing is a write:
 *   `/v1/admin/ai/provider-registry`, `/v1/insights/analytics`,
 *   `/v1/admin/ai/gi-coverage-gaps`, `/v1/admin/ai/usage`.
 *
 * Two deliberate properties, both from the Owner's honesty decisions:
 *   - the dry-run preview calls **no** provider and issues **no** request; it
 *     is the pure `buildPreviewRows` of `../../ai-workbench/workbench.js`, and
 *     every row it produces is labelled as an example,
 *   - cost is a *status*. `costVerified === false` prints "unverified"; no
 *     currency amount is ever shown, and a missing counter prints
 *     "nicht messbar" rather than a zero.
 * The payloads carry no freshness, so the badge says "Alter nicht messbar"
 * instead of an age derived from the local clock.
 */
import { Button, ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useMemo, useState, type ReactNode } from "react";

import {
  WORKBENCH_SMOKE_CHECKLIST,
  buildPreviewRows,
  type WorkbenchResultRow,
  type WorkbenchSelection,
} from "../../../ai-workbench/workbench.js";
import {
  AIA_ANALYTICS_DEFAULT_DAYS,
  AIA_PROVIDER_REGISTRY_KEY,
  AIA_USAGE_KEY,
  AIA_WINDOW_DAYS,
  aiaAnalyticsKey,
  aiaCoverageKey,
  fetchAiaAnalytics,
  fetchAiaCoverage,
  fetchAiaProviderRegistry,
  fetchAiaUsage,
  type AiaCoverageDto,
  type AiaProviderRegistryDto,
  type AiaUsageDto,
  type AiaUsageMetricsDto,
  type AiaWindowDays,
  type FiAnalyticsSummary,
  type GtccResult,
} from "../../api/aia.js";
import {
  ControlFreshBadge,
  ControlHead,
  ControlMono,
  ControlNotMeasurable,
  ControlNote,
  ControlPanel,
  ControlRow,
  GRID_COLUMN_STYLE,
  GRID_MAIN_CLASS,
  asList,
  formatInteger,
  measuredNumber,
  type ControlTone,
} from "../control/ControlPrimitives.js";

/* ---------------------------------------------------------------- queries */

const AIA_STALE_MS = 30_000;
const AIA_REFETCH_MS = 60_000;

const AIA_QUERY_OPTIONS = {
  staleTime: AIA_STALE_MS,
  refetchInterval: AIA_REFETCH_MS,
  refetchOnWindowFocus: false,
} as const;

/** Extracts the successful GET payload without inventing a fallback value. */
function dataOf<T>(query: UseQueryResult<GtccResult<T>>): T | null {
  return query.data !== undefined && query.data.ok ? query.data.data : null;
}

/* ------------------------------------------------------------------ view */

export function AiaView(): ReactNode {
  const { t, locale } = useLocale();
  const [coverageDays, setCoverageDays] = useState<AiaWindowDays>(AIA_ANALYTICS_DEFAULT_DAYS);

  const registryQuery = useQuery({
    queryKey: AIA_PROVIDER_REGISTRY_KEY,
    queryFn: ({ signal }) => fetchAiaProviderRegistry(signal),
    ...AIA_QUERY_OPTIONS,
  });
  const analyticsQuery = useQuery({
    queryKey: aiaAnalyticsKey(AIA_ANALYTICS_DEFAULT_DAYS),
    queryFn: ({ signal }) => fetchAiaAnalytics(AIA_ANALYTICS_DEFAULT_DAYS, signal),
    ...AIA_QUERY_OPTIONS,
  });
  const coverageQuery = useQuery({
    queryKey: aiaCoverageKey(coverageDays),
    queryFn: ({ signal }) => fetchAiaCoverage(coverageDays, signal),
    ...AIA_QUERY_OPTIONS,
  });
  const usageQuery = useQuery({
    queryKey: AIA_USAGE_KEY,
    queryFn: ({ signal }) => fetchAiaUsage(signal),
    ...AIA_QUERY_OPTIONS,
  });

  const registry: AiaProviderRegistryDto | null = dataOf(registryQuery);
  const analytics: FiAnalyticsSummary | null = dataOf(analyticsQuery);
  const coverage: AiaCoverageDto | null = dataOf(coverageQuery);
  const usage: AiaUsageDto | null = dataOf(usageQuery);

  const queries = [registryQuery, analyticsQuery, coverageQuery, usageQuery];
  /* Nur `isPending`: eine mit einem Fehler gesettelte Query traegt kein
     `data` — wer sie als „laedt noch" zaehlt, dreht den Spinner ewig. */
  const allPending = queries.every((query) => query.isPending);
  const anyLoaded = registry !== null || analytics !== null || coverage !== null || usage !== null;
  const deniedOnly = queries.some(
    (query) => query.data !== undefined && !query.data.ok && query.data.kind === "forbidden",
  );

  const head = (
    <ControlHead
      testId="gtcc-aia-head"
      title={t("admin.gtcc.aia.head.title")}
      subtitle={t("admin.gtcc.aia.head.subtitle")}
      fresh={
        /* Keine dieser Nutzlasten traegt `freshnessSeconds` (P10). */
        <ControlFreshBadge testId="gtcc-aia-fresh" text={t("admin.gtcc.aia.fresh.unmeasurable")} />
      }
    />
  );

  if (allPending) {
    return (
      <Stack gap={3} data-testid="gtcc-view-aia">
        {head}
        <div data-testid="gtcc-aia-loading">
          <LoadingState label={t("admin.gtcc.aia.loading")} />
        </div>
      </Stack>
    );
  }

  if (!anyLoaded && deniedOnly) {
    return (
      <Stack gap={3} data-testid="gtcc-view-aia">
        {head}
        <div data-testid="gtcc-aia-failure">
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.aia.forbidden.title")}
            body={t("admin.gtcc.aia.forbidden.body")}
          />
        </div>
      </Stack>
    );
  }

  if (!anyLoaded) {
    return (
      <Stack gap={3} data-testid="gtcc-view-aia">
        {head}
        <div data-testid="gtcc-aia-failure">
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.aia.failure.title")}
            body={t("admin.gtcc.aia.failure.body")}
          />
        </div>
      </Stack>
    );
  }

  return (
    <Stack gap={3} data-testid="gtcc-view-aia">
      {head}
      <div className={GRID_MAIN_CLASS}>
        <div style={GRID_COLUMN_STYLE}>
          <WorkbenchPanel registry={registry} locale={locale} />
          <QuestionAnalyticsPanel analytics={analytics} locale={locale} />
        </div>
        <div style={GRID_COLUMN_STYLE}>
          <CoveragePanel
            coverage={coverage}
            days={coverageDays}
            onDays={setCoverageDays}
            locale={locale}
          />
          <UsagePanel usage={usage} locale={locale} />
        </div>
      </div>
    </Stack>
  );
}

/* ------------------------------------------- 1 · Provider-Workbench (Staging) */

/**
 * The reference's three rows. The dry-run button runs the existing pure
 * preview builder over the registry's own default models — it touches no
 * provider, sends no request, and every produced row carries `preview: true`,
 * which is exactly what the row's label promises.
 */
function WorkbenchPanel(props: {
  registry: AiaProviderRegistryDto | null;
  locale: Parameters<typeof formatInteger>[1];
}): ReactNode {
  const { t } = useLocale();
  const [rows, setRows] = useState<readonly WorkbenchResultRow[] | null>(null);

  const providers = asList(props.registry?.registry?.providers);

  const selections: readonly WorkbenchSelection[] = useMemo(
    () =>
      providers.map((provider) => ({
        provider: provider.id,
        model: provider.defaultModel,
        experimental: provider.experimental === true,
      })),
    [providers],
  );

  const runPreview = useCallback(() => setRows(buildPreviewRows(selections)), [selections]);

  return (
    <ControlPanel testId="gtcc-aia-wb" title={t("admin.gtcc.aia.wb.panel_title")}>
      <ControlRow
        testId="gtcc-aia-wb-case"
        tone="info"
        label={t("admin.gtcc.aia.wb.case")}
        value={
          /* Referenzzeile 963: die Aktion steht als `[…]`-Text am rechten Rand
             derselben Zeile, nicht als gerahmter Knopf. */
          <Button
            variant="secondary"
            size="sm"
            data-testid="gtcc-aia-wb-run"
            onClick={runPreview}
            disabled={selections.length === 0}
          >
            {t("admin.gtcc.aia.wb.run")}
          </Button>
        }
      />
      <ControlRow
        testId="gtcc-aia-wb-allowlist"
        tone={providers.length === 0 ? "muted" : "info"}
        label={t("admin.gtcc.aia.wb.allowlist")}
        value={
          providers.length === 0 ? (
            <ControlNotMeasurable label={t("admin.gtcc.aia.not_measurable")} />
          ) : (
            <ControlMono>{providers.map((provider) => provider.id).join(" · ")}</ControlMono>
          )
        }
      />
      <ControlRow
        testId="gtcc-aia-wb-checklist"
        tone="ok"
        label={t("admin.gtcc.aia.wb.checklist")}
        value={
          /* Gezaehlt, nicht behauptet: die Liste selbst gibt die Punktzahl. */
          <ControlMono>
            {t("admin.gtcc.aia.wb.checklist_value", {
              count: formatInteger(WORKBENCH_SMOKE_CHECKLIST.length, props.locale),
            })}
          </ControlMono>
        }
      />
      {rows === null ? null : (
        <Stack gap={0} data-testid="gtcc-aia-wb-preview">
          {rows.map((row) => (
            <ControlRow
              key={`${row.provider}/${row.model}`}
              testId={`gtcc-aia-wb-preview-${row.provider}`}
              tone={row.status === "ok" ? "ok" : "warn"}
              label={`${row.provider} · ${row.model}`}
              value={
                <ControlMono>
                  {t("admin.gtcc.aia.wb.preview_value", {
                    status: row.status,
                    cost: row.costStatus,
                  })}
                </ControlMono>
              }
            />
          ))}
        </Stack>
      )}
      {rows === null ? null : (
        <ControlNote testId="gtcc-aia-wb-preview-note">
          {t("admin.gtcc.aia.wb.preview_note")}
        </ControlNote>
      )}
    </ControlPanel>
  );
}

/* --------------------------------------------------- 2 · Question Analytics */

/**
 * One CSV line per breakdown bucket — built from the payload already shown.
 * A counter the payload does not carry is written as the empty field, never as
 * a zero: the export says exactly as much as the panel above it (P9).
 */
function analyticsCsv(analytics: FiAnalyticsSummary): string {
  const cell = (value: unknown): string => {
    const measured = measuredNumber(value);
    return measured === null ? "" : String(measured);
  };
  const match = analytics.match as FiAnalyticsSummary["match"] | undefined;
  const feedback = analytics.feedback as FiAnalyticsSummary["feedback"] | undefined;
  const lines = ["section,key,count"];
  lines.push(`match,total,${cell(match?.total)}`);
  lines.push(`match,matched,${cell(match?.matched)}`);
  lines.push(`match,unmatched,${cell(match?.unmatched)}`);
  lines.push(`feedback,helpful,${cell(feedback?.helpful)}`);
  lines.push(`feedback,notHelpful,${cell(feedback?.notHelpful)}`);
  for (const bucket of asList(match?.byLocale)) lines.push(`locale,${bucket.key},${bucket.count}`);
  for (const bucket of asList(match?.bySourceSurface))
    lines.push(`source,${bucket.key},${bucket.count}`);
  for (const bucket of asList(match?.byProviderMode))
    lines.push(`providerMode,${bucket.key},${bucket.count}`);
  return `${lines.join("\n")}\n`;
}

function QuestionAnalyticsPanel(props: {
  analytics: FiAnalyticsSummary | null;
  locale: Parameters<typeof formatInteger>[1];
}): ReactNode {
  const { t } = useLocale();
  const analytics = props.analytics;

  /* „Export CSV" der Referenz: rein clientseitig aus der bereits geladenen
     Aggregat-Nutzlast — kein zusaetzlicher Endpunkt, kein Roh-Text. */
  const exportCsv = useCallback(() => {
    if (analytics === null || typeof document === "undefined" || typeof URL === "undefined") return;
    const blob = new Blob([analyticsCsv(analytics)], { type: "text/csv;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = `gtcc-question-analytics-${analytics.windowDays}d.csv`;
    anchor.click();
    URL.revokeObjectURL(href);
  }, [analytics]);

  /* Eine 200 ohne `match`/`feedback` ist keine Messung — die Zeile sagt dann
     „nicht messbar" statt zu stuerzen oder eine Null zu erfinden (P9/P10). */
  const match = (analytics?.match ?? null) as FiAnalyticsSummary["match"] | null;
  const feedback = (analytics?.feedback ?? null) as FiAnalyticsSummary["feedback"] | null;

  const asked = measuredNumber(match?.total);
  const answered = measuredNumber(match?.matched);
  const helpful = measuredNumber(feedback?.helpful);
  const notHelpful = measuredNumber(feedback?.notHelpful);

  const breakdown =
    match === null
      ? null
      : [
          asList(match.byLocale).length,
          asList(match.bySourceSurface).length,
          asList(match.byProviderMode).length,
        ];

  return (
    <ControlPanel
      testId="gtcc-aia-qa"
      title={`${t("admin.gtcc.aia.qa.title")} · ${t("admin.gtcc.aia.qa.note", {
        days: formatInteger(AIA_ANALYTICS_DEFAULT_DAYS, props.locale),
      })}`}
    >
      <ControlRow
        testId="gtcc-aia-qa-asked"
        tone={asked === null || answered === null ? "muted" : "ok"}
        label={t("admin.gtcc.aia.qa.asked")}
        value={
          asked === null || answered === null ? (
            <ControlNotMeasurable label={t("admin.gtcc.aia.not_measurable")} />
          ) : (
            <ControlMono>
              {t("admin.gtcc.aia.qa.asked_value", {
                asked: formatInteger(asked, props.locale),
                answered: formatInteger(answered, props.locale),
              })}
            </ControlMono>
          )
        }
      />
      <ControlRow
        testId="gtcc-aia-qa-feedback"
        tone={helpful === null || notHelpful === null ? "muted" : "ok"}
        label={t("admin.gtcc.aia.qa.feedback")}
        value={
          helpful === null || notHelpful === null ? (
            <ControlNotMeasurable label={t("admin.gtcc.aia.not_measurable")} />
          ) : (
            <ControlMono>
              {t("admin.gtcc.aia.qa.feedback_value", {
                helpful: formatInteger(helpful, props.locale),
                not_helpful: formatInteger(notHelpful, props.locale),
                reasons: formatInteger(asList(feedback?.byReason).length, props.locale),
              })}
            </ControlMono>
          )
        }
      />
      <ControlRow
        testId="gtcc-aia-qa-breakdown"
        tone={breakdown === null ? "muted" : "info"}
        label={t("admin.gtcc.aia.qa.breakdown")}
        value={
          breakdown === null ? (
            <ControlNotMeasurable label={t("admin.gtcc.aia.not_measurable")} />
          ) : (
            <ControlMono>
              {t("admin.gtcc.aia.qa.breakdown_value", {
                locales: formatInteger(breakdown[0]!, props.locale),
                sources: formatInteger(breakdown[1]!, props.locale),
                modes: formatInteger(breakdown[2]!, props.locale),
              })}
            </ControlMono>
          )
        }
      />
      {/* Die Referenz nennt den Export in der Tafel-Ergaenzung („30 Tage ·
          Export CSV", Referenzzeile 969) und kennt keinen gerahmten Knopf; die
          ausloesbare Fassung steht deshalb als Textzeile unter den Messzeilen. */}
      <div>
        <Button
          variant="secondary"
          size="sm"
          data-testid="gtcc-aia-qa-export"
          onClick={exportCsv}
          disabled={analytics === null}
        >
          {t("admin.gtcc.aia.qa.export")}
        </Button>
      </div>
    </ControlPanel>
  );
}

/* ------------------------------------------------------ 3 · GI Coverage Gaps */

function CoveragePanel(props: {
  coverage: AiaCoverageDto | null;
  days: AiaWindowDays;
  onDays: (days: AiaWindowDays) => void;
  locale: Parameters<typeof formatInteger>[1];
}): ReactNode {
  const { t } = useLocale();
  const view = props.coverage?.coverage ?? null;
  const groups = asList(view?.topQuestions);
  // The reference stops at its aggregate rows; question examples belong to a detail surface.
  const shown = groups.slice(0, 0);
  const total = measuredNumber(view?.total);

  return (
    <ControlPanel testId="gtcc-aia-cov" title={t("admin.gtcc.aia.cov.title")}>
      <ControlRow
        testId="gtcc-aia-cov-total"
        tone={total === null ? "muted" : total > 0 ? "warn" : "ok"}
        label={t("admin.gtcc.aia.cov.unanswered")}
        value={
          total === null ? (
            <ControlNotMeasurable label={t("admin.gtcc.aia.not_measurable")} />
          ) : (
            <ControlMono>
              {t("admin.gtcc.aia.cov.unanswered_value", {
                total: formatInteger(total, props.locale),
                groups: formatInteger(groups.length, props.locale),
              })}
            </ControlMono>
          )
        }
      />
      <ControlRow
        testId="gtcc-aia-cov-split"
        tone={view === null ? "muted" : "info"}
        label={t("admin.gtcc.aia.cov.split")}
        value={
          view === null ? (
            <ControlNotMeasurable label={t("admin.gtcc.aia.not_measurable")} />
          ) : (
            <ControlMono>
              {t("admin.gtcc.aia.cov.split_value", {
                domains: formatInteger(asList(view.byDomain).length, props.locale),
                reasons: formatInteger(asList(view.byReason).length, props.locale),
                outcomes: formatInteger(asList(view.byStatus).length, props.locale),
              })}
            </ControlMono>
          )
        }
      />
      {/* Referenzzeile 978 schreibt das Zeitfenster als eine Textzeile
          („Filter 7/30/90 Tage"); die Auswahl steht deshalb in einer Zeile
          nebeneinander, die gewaehlte Spanne traegt `aria-pressed`. */}
      <div
        role="group"
        aria-label={t("admin.gtcc.aia.cov.filter")}
        data-testid="gtcc-aia-cov-filter"
        style={{ display: "flex", gap: "var(--gt-sys-space-3)", flexWrap: "wrap" }}
      >
        {AIA_WINDOW_DAYS.map((days) => (
          <Button
            key={days}
            variant={days === props.days ? "primary" : "secondary"}
            size="sm"
            data-testid={`gtcc-aia-cov-filter-${days}`}
            aria-pressed={days === props.days}
            onClick={() => props.onDays(days)}
          >
            {t("admin.gtcc.aia.cov.filter_days", {
              days: formatInteger(days, props.locale),
            })}
          </Button>
        ))}
      </div>
      {shown.length === 0 ? null : (
        <Stack gap={0}>
          {shown.map((group, index) => (
            <ControlRow
              /* Der Hash ist der Schluessel; fehlt er in der Antwort, traegt die
                 Position ihn, damit React keine Warnung statt einer Messung
                 ausgibt. */
              key={group.questionHash ?? `row-${index}`}
              testId="gtcc-aia-cov-group"
              tone={group.needsNewTool ? "warn" : "info"}
              /* Bereits serverseitig redigierte Vorschau — nie ein Rohtext. */
              label={group.examplePreview}
              value={
                <ControlMono>
                  {t("admin.gtcc.aia.cov.group_value", {
                    count: formatInteger(group.count, props.locale),
                    domain: group.mappedDomain,
                    reason: group.reasonCategory,
                  })}
                </ControlMono>
              }
            />
          ))}
        </Stack>
      )}
    </ControlPanel>
  );
}

/* --------------------------------------------------- 4 · AI-Nutzung je Tenant */

function usageTone(metrics: AiaUsageMetricsDto | undefined): ControlTone {
  return measuredNumber(metrics?.invocations) === null ? "muted" : "ok";
}

function UsagePanel(props: {
  usage: AiaUsageDto | null;
  locale: Parameters<typeof formatInteger>[1];
}): ReactNode {
  const { t } = useLocale();
  const view = props.usage?.usage ?? null;
  // Tenant detail remains in Tenant 360°, matching the two-row reference panel.
  const metricsText = useCallback(
    (metrics: AiaUsageMetricsDto | undefined): ReactNode => {
      const invocations = measuredNumber(metrics?.invocations);
      const calls = measuredNumber(metrics?.providerCalledCount);
      const tokens = measuredNumber(metrics?.totalTokens);
      if (invocations === null && calls === null && tokens === null) {
        return <ControlNotMeasurable label={t("admin.gtcc.aia.not_measurable")} />;
      }
      const unmeasured = t("admin.gtcc.aia.not_measurable");
      return (
        <ControlMono>
          {t("admin.gtcc.aia.usage.metrics_value", {
            invocations:
              invocations === null ? unmeasured : formatInteger(invocations, props.locale),
            calls: calls === null ? unmeasured : formatInteger(calls, props.locale),
            tokens: tokens === null ? unmeasured : formatInteger(tokens, props.locale),
          })}
        </ControlMono>
      );
    },
    [t, props.locale],
  );

  return (
    <ControlPanel testId="gtcc-aia-usage" title={t("admin.gtcc.aia.usage.title")}>
      <ControlRow
        testId="gtcc-aia-usage-totals"
        tone={view?.totals === undefined ? "muted" : usageTone(view.totals)}
        label={t("admin.gtcc.aia.usage.totals")}
        value={
          view?.totals === undefined ? (
            <ControlNotMeasurable label={t("admin.gtcc.aia.not_measurable")} />
          ) : (
            metricsText(view.totals)
          )
        }
      />
      <ControlRow
        testId="gtcc-aia-usage-cost"
        /* Kosten sind ein Status, nie ein Betrag: `costVerified === false`
           heisst „unverified" und wird so gedruckt (P8). */
        tone="muted"
        label={t("admin.gtcc.aia.usage.cost")}
        value={
          <ControlMono tone="muted">
            {view?.costVerified === true
              ? t("admin.gtcc.aia.usage.cost_verified")
              : t("admin.gtcc.aia.usage.cost_unverified")}
          </ControlMono>
        }
      />
      <ControlNote testId="gtcc-aia-usage-note">{t("admin.gtcc.aia.usage.note")}</ControlNote>
    </ControlPanel>
  );
}

export default AiaView;
